import { apiAIService } from "./api";
import toast from "react-hot-toast";

const MAX_CURRENT_BALANCE_FAILURES = 3;
const CURRENT_BALANCE_RETRY_COOLDOWN_MS = 30 * 1000;
let currentBalanceFailureCount = 0;
let currentBalanceCooldownUntil = 0;
// Cache the last good balance so a breaker-open (cooldown) call can resolve
// instead of throwing — a throw would surface as an error in callers and, when
// combined with retry-on-error logic, drive an infinite error/refetch loop.
let lastCurrentBalance = null;

export const financialService = {
  // Get dashboard analytics
  getDashboardAnalytics: async (period = "7_days", timeUnit = "days") => {
    try {
      const response = await apiAIService.get(
        `/dashboard/analytics?period=${period}&time_unit=${timeUnit}`,
      );
      return response;
    } catch (error) {
      console.error("Failed to fetch dashboard analytics:", error);
      throw new Error("Failed to fetch dashboard data");
    }
  },

  getDashboardAiInsights: async () => {
    try {
      return await apiAIService.get("/dashboard/ai-insights");
    } catch (error) {
      console.error("Failed to fetch AI dashboard insights:", error);
      throw new Error("Failed to fetch AI insights");
    }
  },

  // Transaction APIs
  getTransactions: async (params = {}) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const response = await apiAIService.get(`/transactions?${queryString}`);
      return response;
    } catch (error) {
      console.error("Failed to fetch transactions:", error);
      throw new Error("Failed to fetch transactions");
    }
  },

  createTransaction: async (transactionData) => {
    try {
      const response = await apiAIService.post(
        "/transactions",
        transactionData,
      );
      return response;
    } catch (error) {
      const errorMessage =
        error.response?.data?.detail || "Failed to create transaction";

      throw new Error(errorMessage);
    }
  },

  getTransaction: async (transactionId) => {
    try {
      const response = await apiAIService.get(`/transactions/${transactionId}`);
      return response;
    } catch (error) {
      console.error("Failed to fetch transaction:", error);
      throw new Error("Failed to fetch transaction");
    }
  },

  deleteTransaction: async (transactionId) => {
    try {
      await apiAIService.delete(`/transactions/${transactionId}`);
      return { success: true };
    } catch (error) {
      const errorMessage =
        error.response?.data?.detail || "Failed to delete transaction";

      throw new Error(errorMessage);
    }
  },

  updateTransaction: async (transactionId, transactionData) => {
    try {
      const response = await apiAIService.put(
        `/transactions/${transactionId}`,
        transactionData,
      );
      return response;
    } catch (error) {
      const errorMessage =
        error.response?.data?.detail || "Failed to update transaction";
      throw new Error(errorMessage);
    }
  },

  getTransactionSummary: async (params = {}) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const response = await apiAIService.get(
        `/transactions/summary/overview?${queryString}`,
      );
      return response;
    } catch (error) {
      console.error("Failed to fetch transaction summary:", error);
      throw new Error("Failed to fetch transaction summary");
    }
  },

  getMonthlyReport: async (year, month) => {
    try {
      const response = await apiAIService.get(
        `/transactions/reports/monthly/${year}/${month}`,
      );
      return response;
    } catch (error) {
      console.error("Failed to fetch monthly report:", error);
      throw new Error("Failed to fetch monthly report");
    }
  },

  getCurrentBalance: async () => {
    // Breaker open: resolve quietly with the last cached balance (or null)
    // rather than throwing, so we never make a network call or surface an error
    // while cooling down.
    if (Date.now() < currentBalanceCooldownUntil) {
      return lastCurrentBalance;
    }

    try {
      const response = await apiAIService.get("/transactions/balance/current");
      currentBalanceFailureCount = 0;
      currentBalanceCooldownUntil = 0;
      lastCurrentBalance = response;
      return response;
    } catch (error) {
      currentBalanceFailureCount += 1;
      if (currentBalanceFailureCount >= MAX_CURRENT_BALANCE_FAILURES) {
        currentBalanceCooldownUntil =
          Date.now() + CURRENT_BALANCE_RETRY_COOLDOWN_MS;
        // Log only when the breaker trips — not on every individual attempt —
        // to avoid flooding the console.
        console.error(
          "Current balance fetch failing repeatedly; backing off for",
          CURRENT_BALANCE_RETRY_COOLDOWN_MS / 1000,
          "s",
          error,
        );
      }

      throw new Error("Failed to fetch current balance");
    }
  },

  getExpenseCategories: async () => {
    try {
      const response = await apiAIService.get(
        "/transactions/categories/expenses",
      );
      return response;
    } catch (error) {
      console.error("Failed to fetch expense categories:", error);
      throw new Error("Failed to fetch expense categories");
    }
  },

  getMonthlyIncomeTargets: async () => {
    try {
      const response = await apiAIService.get("/transactions/income/monthly");
      return response;
    } catch (error) {
      console.error("Failed to fetch monthly income targets:", error);
      throw new Error("Failed to fetch monthly income targets");
    }
  },

  // Goals APIs
  getGoals: async () => {
    try {
      const response = await apiAIService.get("/goals");
      return response;
    } catch (error) {
      console.error("Failed to fetch goals:", error);
      throw new Error("Failed to fetch goals");
    }
  },

  getGoal: async (goalId) => {
    try {
      const response = await apiAIService.get(`/goals/${goalId}`);
      return response;
    } catch (error) {
      console.error("Failed to fetch goal:", error);
      throw new Error("Failed to fetch goal");
    }
  },

  createGoal: async (goalData) => {
    try {
      const response = await apiAIService.post("/goals", goalData);
      toast.success("Goal created successfully!");
      return response;
    } catch (error) {
      const errorMessage =
        error.response?.data?.detail || "Failed to create goal";

      throw new Error(errorMessage);
    }
  },

  updateGoal: async (goalId, goalData) => {
    try {
      const response = await apiAIService.put(`/goals/${goalId}`, goalData);
      toast.success("Goal updated successfully!");
      return response;
    } catch (error) {
      const errorMessage =
        error.response?.data?.detail || "Failed to update goal";

      throw new Error(errorMessage);
    }
  },

  deleteGoal: async (goalId) => {
    try {
      const response = await apiAIService.delete(`/goals/${goalId}`);
      return response;
    } catch (error) {
      const errorMessage =
        error.response?.data?.detail || "Failed to delete goal";

      throw new Error(errorMessage);
    }
  },

  getGoalsProgress: async () => {
    try {
      const response = await apiAIService.get("/goals/progress/summary");
      return response;
    } catch (error) {
      console.error("Failed to fetch goals progress:", error);
      throw new Error("Failed to fetch goals progress");
    }
  },

  addFundsToGoal: async (goalId, transactionData) => {
    try {
      const response = await apiAIService.post(
        `/goals/${goalId}/add-funds`,
        transactionData,
      );
      toast.success("Funds added to goal successfully!");
      return response;
    } catch (error) {
      const errorMessage =
        error.response?.data?.detail || "Failed to add funds to goal";

      throw new Error(errorMessage);
    }
  },

  withdrawFundsFromGoal: async (goalId, transactionData) => {
    try {
      const response = await apiAIService.post(
        `/goals/${goalId}/withdraw-funds`,
        transactionData,
      );
      toast.success("Funds withdrawn from goal successfully!");
      return response;
    } catch (error) {
      const errorMessage =
        error.response?.data?.detail || "Failed to withdraw funds from goal";

      throw new Error(errorMessage);
    }
  },

  getGoalTransactions: async (goalId, params = {}) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const response = await apiAIService.get(
        `/goals/${goalId}/transactions?${queryString}`,
      );
      return response;
    } catch (error) {
      console.error("Failed to fetch goal transactions:", error);
      throw new Error("Failed to fetch goal transactions");
    }
  },

  getGoalTransactionSummary: async (goalId) => {
    try {
      const response = await apiAIService.get(
        `/goals/${goalId}/transaction-summary`,
      );
      return response;
    } catch (error) {
      console.error("Failed to fetch goal transaction summary:", error);
      throw new Error("Failed to fetch goal transaction summary");
    }
  },

  // Chat APIs
  sendMessage: async (message, conversationId = null) => {
    try {
      const response = await apiAIService.post("/chat/financial-advice", {
        message,
        conversation_id: conversationId,
      });
      return response;
    } catch (error) {
      console.error("Failed to send message:", error);
      throw new Error("Failed to send message");
    }
  },

  sendMessageStream: async (message, conversationId = null, onChunk, signal) => {
    try {
      // Get the base URL from the apiAIService configuration
      const baseURL =
        import.meta.env.VITE_API_AI_BASE_URL || "http://localhost:8000";
      const user = JSON.parse(localStorage.getItem("user"));
      const token = user?.aiToken;

      const response = await fetch(`${baseURL}/chat/financial-advice-stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          message,
          conversation_id: conversationId,
        }),
        signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let finalConversationId = conversationId;
      let buffer = ""; // Buffer for incomplete data

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        buffer += chunk;

        // Process complete lines from buffer
        const lines = buffer.split("\n");
        // Keep the last incomplete line in buffer
        buffer = lines.pop() || "";

        for (const line of lines) {
          const trimmedLine = line.trim();
          if (trimmedLine.startsWith("data: ")) {
            const jsonStr = trimmedLine.slice(6);

            // Skip empty data
            if (!jsonStr || jsonStr === "") continue;

            try {
              const data = JSON.parse(jsonStr);

              // Track conversation_id if it's provided in any chunk
              if (data.conversation_id) {
                finalConversationId = data.conversation_id;
              }

              if (onChunk) {
                onChunk(data);
              }
            } catch (e) {
              // If parse fails, it might be incomplete - add back to buffer with newline
              if (
                e.message.includes("Unterminated") ||
                e.message.includes("Unexpected end")
              ) {
                buffer = trimmedLine + "\n" + buffer;
              } else {
                console.error(
                  "Error parsing SSE data:",
                  e,
                  "Data:",
                  jsonStr.substring(0, 100),
                );
              }
            }
          }
        }
      }

      // Process any remaining data in buffer
      if (buffer.trim().startsWith("data: ")) {
        try {
          const jsonStr = buffer.trim().slice(6);
          if (jsonStr) {
            const data = JSON.parse(jsonStr);
            if (data.conversation_id) {
              finalConversationId = data.conversation_id;
            }
            if (onChunk) {
              onChunk(data);
            }
          }
        } catch (e) {
          console.error("Error parsing remaining SSE buffer:", e);
        }
      }

      // Return conversation info after streaming completes
      return {
        conversation_id: finalConversationId,
        success: true,
      };
    } catch (error) {
      if (error?.name === "AbortError") {
        throw error;
      }
      console.error("Failed to send streaming message:", error);
      throw new Error("Failed to send streaming message");
    }
  },

  getConversations: async () => {
    try {
      const response = await apiAIService.get("/chat/conversations");
      // Ensure we always return an array
      return Array.isArray(response) ? response : [];
    } catch (error) {
      console.error("Failed to fetch conversations:", error);
      throw new Error("Failed to fetch conversations");
    }
  },

  getUserConversations: async (params = {}) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const response = await apiAIService.get(
        `/chat/user-conversations?${queryString}`,
      );
      return response;
    } catch (error) {
      console.error("Failed to fetch user conversations:", error);

      // More specific error messages
      if (error.code === "ECONNREFUSED" || error.code === "NETWORK_ERROR") {
        throw new Error(
          "Unable to connect to server. Please check your connection.",
        );
      }
      if (error.response?.status === 401) {
        throw new Error("Authentication required. Please log in again.");
      }
      if (error.response?.status >= 500) {
        throw new Error("Server error. Please try again later.");
      }

      throw new Error(
        error.response?.data?.message || "Failed to fetch conversations",
      );
    }
  },

  getConversation: async (conversationId) => {
    try {
      const response = await apiAIService.get(
        `/chat/conversation/${conversationId}`,
      );
      return response;
    } catch (error) {
      console.error("Failed to fetch conversation:", error);
      throw new Error("Failed to fetch conversation");
    }
  },

  getConversationDetails: async (conversationId, includeMetadata = false) => {
    try {
      const response = await apiAIService.get(
        `/chat/conversation-details/${conversationId}?include_metadata=${includeMetadata}`,
      );
      return response;
    } catch (error) {
      console.error("Failed to fetch conversation details:", error);
      throw new Error("Failed to fetch conversation details");
    }
  },

  startNewConversation: async () => {
    try {
      const response = await apiAIService.post("/chat/start-new-conversation");
      return response;
    } catch (error) {
      console.error("Failed to start new conversation:", error);
      throw new Error("Failed to start new conversation");
    }
  },

  switchConversation: async (conversationId) => {
    try {
      const response = await apiAIService.post(
        `/chat/switch-conversation/${conversationId}`,
      );
      return response;
    } catch (error) {
      console.error("Failed to switch conversation:", error);
      throw new Error("Failed to switch conversation");
    }
  },

  deleteConversation: async (conversationId) => {
    try {
      const response = await apiAIService.delete(
        `/chat/conversation/${conversationId}`,
      );
      return response;
    } catch (error) {
      const errorMessage =
        error.response?.data?.detail || "Failed to delete conversation";

      throw new Error(errorMessage);
    }
  },

  renameConversation: async (conversationId, newTitle) => {
    try {
      const response = await apiAIService.put(
        `/chat/conversation/${conversationId}/rename`,
        {
          new_title: newTitle,
        },
      );

      return response;
    } catch (error) {
      const errorMessage =
        error.response?.data?.detail || "Failed to rename conversation";
    }
  },

  getChatHistory: async (params = {}) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const response = await apiAIService.get(`/chat/history?${queryString}`);
      return response;
    } catch (error) {
      console.error("Failed to fetch chat history:", error);
      throw new Error("Failed to fetch chat history");
    }
  },

  clearMemory: async (conversationOnly = false) => {
    try {
      const response = await apiAIService.delete(
        `/chat/clear-memory?conversation_only=${conversationOnly}`,
      );

      return response;
    } catch (error) {
      const errorMessage =
        error.response?.data?.detail || "Failed to clear memory";

      throw new Error(errorMessage);
    }
  },

  getChatStatistics: async () => {
    try {
      const response = await apiAIService.get("/chat/statistics");
      return response;
    } catch (error) {
      console.error("Failed to fetch chat statistics:", error);
      throw new Error("Failed to fetch chat statistics");
    }
  },

  // Get financial dashboard data
  getFinancialData: async (userId) => {
    return await apiAIService.get(`/financial/data/${userId}`);
  },

  // Budget APIs
  getBudgetCategories: async () => {
    try {
      const response = await apiAIService.get("/budgets/categories");
      return response;
    } catch (error) {
      console.error("Failed to fetch budget categories:", error);
      throw new Error("Failed to fetch budget categories");
    }
  },

  getBudgets: async (activeOnly = true) => {
    try {
      const response = await apiAIService.get(
        `/budgets?active_only=${activeOnly}`,
      );
      return response;
    } catch (error) {
      console.error("Failed to fetch budgets:", error);
      throw new Error("Failed to fetch budgets");
    }
  },

  getActiveBudget: async () => {
    try {
      // Returns the single active monthly budget, or null if none exists.
      const response = await apiAIService.get("/budgets/active");
      return response;
    } catch (error) {
      console.error("Failed to fetch active budget:", error);
      throw new Error("Failed to fetch active budget");
    }
  },

  getBudget: async (budgetId) => {
    try {
      const response = await apiAIService.get(`/budgets/${budgetId}`);
      return response;
    } catch (error) {
      console.error("Failed to fetch budget:", error);
      throw new Error("Failed to fetch budget");
    }
  },

  getBudgetByCategory: async (category) => {
    try {
      const response = await apiAIService.get(
        `/budgets/category/${encodeURIComponent(category)}`,
      );
      return response;
    } catch (error) {
      console.error("Failed to fetch budget by category:", error);
      throw new Error("Failed to fetch budget by category");
    }
  },

  createBudget: async (budgetData) => {
    try {
      const response = await apiAIService.post("/budgets", budgetData);
      toast.success("Budget created successfully!");
      return response;
    } catch (error) {
      const errorMessage =
        error.response?.data?.detail || "Failed to create budget";
      throw new Error(errorMessage);
    }
  },

  updateBudget: async (budgetId, budgetData) => {
    try {
      const response = await apiAIService.put(
        `/budgets/${budgetId}`,
        budgetData,
      );
      toast.success("Budget updated successfully!");
      return response;
    } catch (error) {
      const errorMessage =
        error.response?.data?.detail || "Failed to update budget";
      throw new Error(errorMessage);
    }
  },

  deleteBudget: async (budgetId) => {
    try {
      await apiAIService.delete(`/budgets/${budgetId}`);
      toast.success("Budget deleted successfully!");
      return { success: true };
    } catch (error) {
      const errorMessage =
        error.response?.data?.detail || "Failed to delete budget";
      throw new Error(errorMessage);
    }
  },

  resetBudgetPeriod: async (budgetId) => {
    try {
      const response = await apiAIService.post(`/budgets/${budgetId}/reset`);
      toast.success("Budget period reset successfully!");
      return response;
    } catch (error) {
      const errorMessage =
        error.response?.data?.detail || "Failed to reset budget period";
      throw new Error(errorMessage);
    }
  },

  recalculateBudgets: async () => {
    try {
      const response = await apiAIService.post("/budgets/recalculate");
      toast.success("Budgets recalculated successfully!");
      return response;
    } catch (error) {
      const errorMessage =
        error.response?.data?.detail || "Failed to recalculate budgets";
      throw new Error(errorMessage);
    }
  },

  // AI Chat
  sendAIMessage: async (message, context = {}) => {
    return await apiAIService.post("/ai/chat", { message, context });
  },

  // Analytics
  getAnalytics: async (userId, timeRange = "3months") => {
    return await apiAIService.get(
      `/analytics/${userId}?timeRange=${timeRange}`,
    );
  },

  // Bank connections
  connectBank: async (plaidToken) => {
    return await apiAIService.post("/bank/connect", { plaidToken });
  },

  disconnectBank: async (accountId) => {
    return await apiAIService.delete(`/bank/disconnect/${accountId}`);
  },

  // Subscription
  updateSubscription: async (planId, paymentMethod) => {
    return await apiAIService.post("/subscription/update", {
      planId,
      paymentMethod,
    });
  },

  cancelSubscription: async () => {
    return await apiAIService.post("/subscription/cancel");
  },
};

// Token and rewards related functions
export const getTokenBalance = async () => {
  try {
    // Try the main endpoint first
    const response = await apiAIService.get("/tokens/balance");
    return response;
  } catch (error) {
    console.warn(
      "Main token balance endpoint failed, trying fast endpoint:",
      error.message,
    );

    // Fallback to fast endpoint if main endpoint times out or fails
    try {
      const fastResponse = await apiAIService.get("/tokens/balance/fast");
      // Convert fast response to match expected format
      return {
        fin_token_balance: fastResponse.fin_token_balance,
        last_updated: fastResponse.last_updated,
        total_earned: fastResponse.fin_token_balance, // Approximation
        recent_activity: "Fast mode - recent activity unavailable",
      };
    } catch (fastError) {
      console.error("Fast token balance endpoint also failed:", fastError);
      throw error; // Throw original error
    }
  }
};

export const getTokenHistory = async (limit = 50, offset = 0) => {
  try {
    const response = await apiAIService.get(
      `/tokens/history?limit=${limit}&offset=${offset}`,
    );
    return response;
  } catch (error) {
    console.error("Error fetching token history:", error);
    throw error;
  }
};

export const getTokenStatistics = async () => {
  try {
    const response = await apiAIService.get("/tokens/statistics");
    return response;
  } catch (error) {
    console.error("Error fetching token statistics:", error);
    throw error;
  }
};

export const getAvailableRewards = async () => {
  try {
    const response = await apiAIService.get("/tokens/rewards/available");
    return response;
  } catch (error) {
    console.error("Error fetching available rewards:", error);
    throw error;
  }
};

export const getTransactionMilestoneProgress = async () => {
  try {
    const response = await apiAIService.get(
      "/tokens/milestones/transaction-progress",
    );
    return response;
  } catch (error) {
    console.error("Error fetching transaction milestone progress:", error);
    throw error;
  }
};

export const getTokensEarnedToday = async () => {
  try {
    const response = await apiAIService.get("/tokens/earnings/today");
    return response;
  } catch (error) {
    console.error("Error fetching today's earnings:", error);
    throw error;
  }
};

export const getTokensEarnedThisWeek = async () => {
  try {
    const response = await apiAIService.get("/tokens/earnings/week");
    return response;
  } catch (error) {
    console.error("Error fetching this week's earnings:", error);
    throw error;
  }
};

export const getDailyWeeklyEarnings = async () => {
  try {
    const response = await apiAIService.get("/tokens/earnings/daily-weekly");
    return response;
  } catch (error) {
    console.error("Error fetching daily/weekly earnings:", error);
    throw error;
  }
};
